import Link from "../../link";
import type { ReactNode } from "react";
import { VITALS, type VitalName } from "../../lib/analytics";
import type { Filters, Report } from "../../lib/stats-report-core";

// The Statistics page's panels. Server-rendered; rows link to the same page with that filter applied.

const n = (v: number) => v.toLocaleString("en-US");
const duration = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${Math.round(s)}s`);
const formatKpi = (value: number, format: Report["kpis"][number]["format"]) =>
  format === "number" ? n(value) : format === "decimal" ? value.toFixed(1) : format === "percent" ? `${value.toFixed(1)}%` : value ? duration(value) : "–";

/** A link to the Statistics page with one filter changed. */
export function filterHref(filters: Filters, changes: Partial<Filters>) {
  const next = { ...filters, ...changes };
  const params = new URLSearchParams();
  if (next.range === "custom") {
    params.set("from", next.from);
    params.set("to", next.to);
  } else if (next.range !== "30d") params.set("range", next.range);
  for (const key of ["page", "source", "device", "region"] as const) if (next[key]) params.set(key, next[key]);
  return `/admin/stats${params.size ? `?${params}` : ""}`;
}

export function Panel({ title, hint, children, wide = false }: { title: string; hint?: string; children: ReactNode; wide?: boolean }) {
  return (
    <section className={`rounded-2xl border border-midnight/10 bg-sand p-4 sm:p-5 ${wide ? "lg:col-span-2" : ""}`}>
      <h2 className="text-lg font-bold">{title}</h2>
      {hint && <p className="mt-1 text-sm text-midnight/70">{hint}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

export const Empty = ({ children = "No data for this period yet." }: { children?: ReactNode }) => <p className="py-4 text-sm text-midnight/70">{children}</p>;

export function Kpis({ kpis }: { kpis: Report["kpis"] }) {
  return (
    <ul className="grid grid-cols-2 gap-3 md:grid-cols-3">
      {kpis.map((k) => (
        <li key={k.label} className="rounded-2xl border border-midnight/10 bg-sand p-4">
          <p className="text-sm text-midnight/70">{k.label}</p>
          <p className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">{formatKpi(k.value, k.format)}</p>
          {k.change !== null && (
            <p className={`mt-1 font-mono text-xs ${k.change > 0 ? "text-teal" : k.change < 0 ? "text-red-800" : "text-midnight/70"}`}>
              {k.change > 0 ? "▲" : k.change < 0 ? "▼" : "•"} {Math.abs(k.change)}% vs previous period
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Visitors (dark) and page views (light) over time. */
export function Chart({ buckets, unit }: { buckets: Report["buckets"]; unit: Report["unit"] }) {
  const max = Math.max(1, ...buckets.map((b) => b.views));
  const w = 10;
  const h = 120;
  const label = (key: string) => (unit === "hour" ? key : new Date(`${key}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }));
  const ticks = [0, Math.floor((buckets.length - 1) / 2), buckets.length - 1].filter((v, i, a) => a.indexOf(v) === i);
  return (
    <div>
      <svg viewBox={`0 0 ${buckets.length * w} ${h}`} preserveAspectRatio="none" className="h-44 w-full" role="img" aria-label={`Visitors and page views per ${unit}`}>
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1="0" x2={buckets.length * w} y1={h - f * h} y2={h - f * h} className="stroke-midnight/10" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
        ))}
        {buckets.map((b, i) => (
          <g key={b.key}>
            <title>{`${unit === "week" ? "Week of " : ""}${label(b.key)}: ${b.visitors} visitors, ${b.views} page views`}</title>
            <rect x={i * w + 1} y={h - (b.views / max) * h} width={w - 2} height={(b.views / max) * h} className="fill-teal/25" />
            <rect x={i * w + 2.5} y={h - (b.visitors / max) * h} width={w - 5} height={(b.visitors / max) * h} className="fill-midnight" />
          </g>
        ))}
      </svg>
      <div className="mt-1 flex justify-between font-mono text-[0.65rem] text-midnight/70">
        {ticks.map((t) => (
          <span key={t}>{label(buckets[t].key)}</span>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-sm">
        <span className="flex items-center gap-2">
          <span className="size-3 rounded-sm bg-midnight" /> Visitors
        </span>
        <span className="flex items-center gap-2">
          <span className="size-3 rounded-sm bg-teal/25" /> Page views
        </span>
      </div>
      <details className="mt-3 text-sm">
        <summary className="cursor-pointer py-2 text-teal underline underline-offset-4">Show the numbers</summary>
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-start">
            <thead>
              <tr className="text-midnight/70">
                <th className="py-1 text-start font-normal">{unit === "hour" ? "Hour (Cairo)" : unit === "week" ? "Week of" : "Day"}</th>
                <th className="py-1 text-end font-normal">Visitors</th>
                <th className="py-1 text-end font-normal">Views</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => (
                <tr key={b.key} className="border-t border-midnight/10">
                  <td className="py-1">{label(b.key)}</td>
                  <td className="py-1 text-end">{b.visitors}</td>
                  <td className="py-1 text-end">{b.views}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

type Row = { key: string; label: string; href?: string; cells: ReactNode[]; value: number };

/** A ranked table with proportional bars; rows can link to a filtered view. */
export function RankTable({ head, rows, limit = 10 }: { head: string[]; rows: Row[]; limit?: number }) {
  if (!rows.length) return <Empty />;
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <table className="w-full table-fixed text-sm">
      <thead>
        <tr className="text-midnight/70">
          {head.map((h, i) => (
            <th key={h} className={`pb-2 font-normal ${i === 0 ? "w-[55%] text-start" : "text-end"}`}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, limit).map((r) => (
          <tr key={r.key} className="border-t border-midnight/10">
            <td className="relative py-2 pe-2">
              <span aria-hidden className="absolute inset-y-1 start-0 rounded bg-teal/10" style={{ width: `${(r.value / max) * 100}%` }} />
              {r.href ? (
                <Link href={r.href} scroll={false} className="relative block truncate py-0.5 ps-2 hover:text-teal" title={`Show only: ${r.label}`}>
                  {r.label}
                </Link>
              ) : (
                <span className="relative block truncate py-0.5 ps-2">{r.label}</span>
              )}
            </td>
            {r.cells.map((c, i) => (
              <td key={i} className="py-2 text-end font-mono text-xs">
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export const fmtDuration = (s: number | null) => (s ? duration(s) : "–");

const RATING = { good: "text-teal", ok: "text-amber-700", poor: "text-red-800" };
export function Vitals({ vitals }: { vitals: Report["vitals"] }) {
  if (!vitals.some((v) => v.samples)) return <Empty>No speed measurements yet. They arrive as people visit.</Empty>;
  const rate = (name: VitalName, v: number | null) => (v === null ? null : v <= VITALS[name].good ? "good" : v <= VITALS[name].poor ? "ok" : "poor");
  return (
    <ul className="flex flex-col gap-4">
      {vitals.map((v) => {
        const meta = VITALS[v.name];
        const r = rate(v.name, v.all);
        return (
          <li key={v.name}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-bold">{meta.label}</span>
              <span className={`font-mono text-lg font-bold ${r ? RATING[r] : ""}`}>
                {v.all === null ? "–" : meta.format(v.all)} {r && <span className="text-xs font-normal">({r === "good" ? "good" : r === "ok" ? "needs work" : "poor"})</span>}
              </span>
            </div>
            <p className="mt-1 font-mono text-xs text-midnight/70">
              Phone {v.phone === null ? "–" : meta.format(v.phone)} · Tablet {v.tablet === null ? "–" : meta.format(v.tablet)} · Desktop {v.desktop === null ? "–" : meta.format(v.desktop)} · {v.samples} measurements
            </p>
          </li>
        );
      })}
    </ul>
  );
}
